import * as geojson from "geojson";

/** The magic wand, in the browser. The run's photo is already on the page
 *  as PNG chunks with their corners in lng/lat (the same raster the server
 *  reads), so a click can grow a region of similar pixels here and only the
 *  finished polygon goes to the server. Transparent pixels (outside the
 *  area) are off the photo. */

export type Overlay = { url: string; coordinates: number[][] };

/** Only this many pixels either side of the click are looked at, as on the server. */
const WINDOW_HALF_PX = 1500;
/** Largest region one click may grow. */
const MAX_REGION_PX = 4_000_000;

/** Decoded chunks kept for the next click: a 2000 px chunk is 16 MB, and
 *  one click's 3000 px window touches up to nine. */
const MAX_CACHED = 9;
const images = new Map<string, Promise<HTMLImageElement>>();

const loadImage = (url: string) => {
  const p = images.get(url);
  if (p) {
    // Most recently used last, so the oldest goes first.
    images.delete(url);
    images.set(url, p);
    return p;
  }
  const loading: Promise<HTMLImageElement> = new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    // A failed load is not kept: the next click tries again.
    img.onerror = () => { if (images.get(url) === loading) images.delete(url); reject(new Error("Could not load the photo.")); };
    img.src = url;
  });
  images.set(url, loading);
  while (images.size > MAX_CACHED) images.delete(images.keys().next().value!);
  return loading;
};

/** Pixel (u, v) of a chunk <-> lng/lat, from its top-left, top-right and
 *  bottom-left corners (an affine; the chunks come off a UTM grid). */
interface Frame { tl: number[]; ex: number[]; ey: number[]; w: number; h: number }
const frameOf = (o: Overlay, w: number, h: number): Frame => {
  const [tl, tr, , bl] = o.coordinates;
  return { tl, ex: [(tr[0] - tl[0]) / w, (tr[1] - tl[1]) / w], ey: [(bl[0] - tl[0]) / h, (bl[1] - tl[1]) / h], w, h };
};
const toLngLat = (f: Frame, u: number, v: number): [number, number] => [f.tl[0] + u * f.ex[0] + v * f.ey[0], f.tl[1] + u * f.ex[1] + v * f.ey[1]];
const toPixel = (f: Frame, lng: number, lat: number): [number, number] => {
  const dx = lng - f.tl[0], dy = lat - f.tl[1];
  const det = f.ex[0] * f.ey[1] - f.ex[1] * f.ey[0];
  return [(dx * f.ey[1] - dy * f.ey[0]) / det, (f.ex[0] * dy - f.ex[1] * dx) / det];
};

/** Is the point inside the chunk's quadrilateral? Needs no image. */
const inQuad = (o: Overlay, lng: number, lat: number) => {
  const q = o.coordinates;
  let inside = false;
  for (let i = 0, j = q.length - 1; i < q.length; j = i++) {
    const xi = q[i][0], yi = q[i][1], xj = q[j][0], yj = q[j][1];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

/** Grow the region like the clicked pixel. Resolves to a polygon in
 *  lng/lat, or null when the click is off the photo or the photo cannot be
 *  read here (not loaded, or the browser refuses its pixels): the caller
 *  falls back to the server then. Throws only when the region would be too
 *  big. */
export async function wandRegion(overlays: Overlay[], lng: number, lat: number, tolerance: number): Promise<geojson.Polygon | null> {
  const base = overlays.find((o) => inQuad(o, lng, lat));
  if (!base) return null;
  let baseImg: HTMLImageElement;
  try { baseImg = await loadImage(base.url); } catch { return null; }
  const frame = frameOf(base, baseImg.naturalWidth, baseImg.naturalHeight);
  const [cu, cv] = toPixel(frame, lng, lat);
  // The window around the click, in the base chunk's pixel space; the
  // neighbouring chunks that overlap it are drawn in at their offsets.
  const wx0 = Math.floor(cu) - WINDOW_HALF_PX, wy0 = Math.floor(cv) - WINDOW_HALF_PX;
  const ww = 2 * WINDOW_HALF_PX, wh = 2 * WINDOW_HALF_PX;
  const placed: { o: Overlay; x: number; y: number }[] = [];
  for (const o of overlays) {
    const [ox, oy] = toPixel(frame, o.coordinates[0][0], o.coordinates[0][1]);
    const [ex, ey] = toPixel(frame, o.coordinates[2][0], o.coordinates[2][1]);
    if (ex < wx0 || ox > wx0 + ww || ey < wy0 || oy > wy0 + wh) continue;
    placed.push({ o, x: Math.round(ox), y: Math.round(oy) });
  }
  let imgs: HTMLImageElement[];
  try { imgs = await Promise.all(placed.map((p) => loadImage(p.o.url))); } catch { return null; }
  const canvas = document.createElement("canvas");
  canvas.width = ww; canvas.height = wh;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  placed.forEach((p, i) => ctx.drawImage(imgs[i], p.x - wx0, p.y - wy0));
  let data: Uint8ClampedArray;
  // A photo served without CORS headers taints the canvas.
  try { data = ctx.getImageData(0, 0, ww, wh).data; } catch { return null; }
  const sx = Math.floor(cu) - wx0, sy = Math.floor(cv) - wy0;
  const at = (x: number, y: number) => (y * ww + x) * 4;
  if (data[at(sx, sy) + 3] === 0) return null;

  // The seed colour is the 3x3 mean around the click; the server does the same.
  let sr = 0, sg = 0, sb = 0, n = 0;
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const x = sx + dx, y = sy + dy;
    if (x < 0 || y < 0 || x >= ww || y >= wh) continue;
    const o = at(x, y);
    if (data[o + 3] === 0) continue;
    sr += data[o]; sg += data[o + 1]; sb += data[o + 2]; n++;
  }
  sr /= n; sg /= n; sb /= n;
  const limit = (Math.max(0, Math.min(100, tolerance)) / 100) * 255 * 0.6;
  const limit2 = limit * limit;
  const mask = new Uint8Array(ww * wh);
  const stack: number[] = [sy * ww + sx];
  let count = 0;
  while (stack.length) {
    const i = stack.pop()!;
    if (mask[i]) continue;
    const x = i % ww, y = (i - x) / ww;
    const o = i * 4;
    if (data[o + 3] === 0) continue;
    const dr = data[o] - sr, dg = data[o + 1] - sg, db = data[o + 2] - sb;
    if (dr * dr + dg * dg + db * db > limit2) continue;
    mask[i] = 1;
    if (++count > MAX_REGION_PX) throw new Error("That picks up too much; lower the tolerance.");
    if (x > 0) stack.push(i - 1);
    if (x < ww - 1) stack.push(i + 1);
    if (y > 0) stack.push(i - ww);
    if (y < wh - 1) stack.push(i + ww);
    if (x > 0 && y > 0) stack.push(i - ww - 1);
    if (x < ww - 1 && y > 0) stack.push(i - ww + 1);
    if (x > 0 && y < wh - 1) stack.push(i + ww - 1);
    if (x < ww - 1 && y < wh - 1) stack.push(i + ww + 1);
  }
  fillHoles(mask, ww, wh);
  const ring = traceOutline(mask, ww, wh);
  if (!ring || ring.length < 4) return null;
  const simplified = rdp(ring, 0.75);
  const coords = simplified.map(([x, y]) => toLngLat(frame, x + wx0, y + wy0));
  coords.push(coords[0]);
  return { type: "Polygon", coordinates: [coords] };
}

/** Anything not reachable from the window's edge without crossing the
 *  region is a hole in it: fill it, as the server does. */
function fillHoles(mask: Uint8Array, w: number, h: number) {
  const outside = new Uint8Array(w * h);
  const stack: number[] = [];
  for (let x = 0; x < w; x++) { stack.push(x); stack.push((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { stack.push(y * w); stack.push(y * w + w - 1); }
  while (stack.length) {
    const i = stack.pop()!;
    if (outside[i] || mask[i]) continue;
    outside[i] = 1;
    const x = i % w, y = (i - x) / w;
    if (x > 0) stack.push(i - 1);
    if (x < w - 1) stack.push(i + 1);
    if (y > 0) stack.push(i - w);
    if (y < h - 1) stack.push(i + w);
  }
  for (let i = 0; i < mask.length; i++) if (!mask[i] && !outside[i]) mask[i] = 1;
}

/** The outer boundary of the region as a ring of pixel-corner points:
 *  every cell edge with the region on one side only, chained into loops;
 *  the longest loop is the outline (holes were filled). */
function traceOutline(mask: Uint8Array, w: number, h: number): [number, number][] | null {
  const vw = w + 1;
  const next = new Map<number, number[]>();
  const add = (a: number, b: number) => { const l = next.get(a); if (l) l.push(b); else next.set(a, [b]); };
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!mask[y * w + x]) continue;
      const tl = y * vw + x, tr = tl + 1, bl = tl + vw, br = bl + 1;
      if (y === 0 || !mask[(y - 1) * w + x]) add(tl, tr);
      if (x === w - 1 || !mask[y * w + x + 1]) add(tr, br);
      if (y === h - 1 || !mask[(y + 1) * w + x]) add(br, bl);
      if (x === 0 || !mask[y * w + x - 1]) add(bl, tl);
    }
  }
  let best: number[] | null = null;
  for (const [start, outs] of next) {
    while (outs.length) {
      const loop = [start];
      let cur = outs.pop()!;
      let guard = 0;
      while (cur !== start && guard++ < 10_000_000) {
        loop.push(cur);
        const l = next.get(cur);
        if (!l || !l.length) break;
        cur = l.pop()!;
      }
      if (!best || loop.length > best.length) best = loop;
    }
  }
  return best ? best.map((v) => [v % vw, Math.floor(v / vw)] as [number, number]) : null;
}

/** Ramer–Douglas–Peucker on a closed ring (first point kept). */
function rdp(pts: [number, number][], eps: number): [number, number][] {
  const n = pts.length;
  if (n < 4) return pts.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1; keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [s, e] = stack.pop()!;
    const [ax, ay] = pts[s], dx = pts[e][0] - ax, dy = pts[e][1] - ay, L = Math.hypot(dx, dy);
    let md = 0, mi = -1;
    for (let i = s + 1; i < e; i++) {
      const d = L ? Math.abs(dx * (pts[i][1] - ay) - dy * (pts[i][0] - ax)) / L : Math.hypot(pts[i][0] - ax, pts[i][1] - ay);
      if (d > md) { md = d; mi = i; }
    }
    if (mi > 0 && md > eps) { keep[mi] = 1; stack.push([s, mi], [mi, e]); }
  }
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[i]);
  return out;
}
